import React, { useState, useEffect } from 'react';
import { useAuth } from '../USER/Auth/AuthContext';
import { getEarnings, getFuelings, getLatestFueling, getBikeData, getDriverWallet, getMinWalletBalance } from '../DB/supabaseServices';
import { useGlobalGPS } from '../GPS/GPSContext';
import { getDriverStatus } from '../../services/api/diary-ui';

import { Link } from 'react-router-dom';
import { FaShieldAlt, FaUsers, FaDollarSign, FaBell, FaHeadset, FaIdCard, FaCar, FaList, FaHeart, FaPlay, FaCalendarAlt, FaFileAlt, FaMoneyBillWave, FaCog } from 'react-icons/fa';

import HeaderSection from './HeaderSection';
import InstallBanner from './InstallBanner';
import HomeModals from './HomeModals';

import AdminView from './views/AdminView';
import PassengerView from './views/PassengerView';
import DriverView from './views/DriverView';
import { DRIVER_STATUSES } from '../../constants';
import './Home.css';

const AdminSidebarLinks = () => (
    <div className="sidebar-links-container">
        <div className="sidebar-section">Administración</div>
        <Link to="/admin" className="sidebar-link"><FaShieldAlt size={16} /> Panel Admin</Link>
        <Link to="/admin/users" className="sidebar-link"><FaUsers size={16} /> Usuarios</Link>
        <Link to="/admin/pagos" className="sidebar-link"><FaDollarSign size={16} /> Pagos</Link>
        <div className="sidebar-section">General</div>
        <Link to="/notificaciones" className="sidebar-link"><FaBell size={16} /> Notificaciones</Link>
        <Link to="/soporte" className="sidebar-link"><FaHeadset size={16} /> Soporte</Link>
        <Link to="/perfil" className="sidebar-link"><FaIdCard size={16} /> Mi Perfil</Link>
    </div>
);

const PassengerSidebarLinks = () => (
    <div className="sidebar-links-container">
        <div className="sidebar-section">Servicios</div>
        <Link to="/solicitar-servicio" className="sidebar-link"><FaCar size={16} /> Solicitar Servicio</Link>
        <Link to="/historial-servicios" className="sidebar-link"><FaList size={16} /> Historial</Link>
        <div className="sidebar-section">Cuenta</div>
        <Link to="/perfil" className="sidebar-link"><FaIdCard size={16} /> Mi Perfil</Link>
        <Link to="/registro-conductor" className="sidebar-link"><FaCar size={16} /> Ser Conductor</Link>
        <Link to="/config" className="sidebar-link"><FaCog size={16} /> Configuración</Link>
        <Link to="/favoritos" className="sidebar-link"><FaHeart size={16} /> Favoritos</Link>
        <Link to="/soporte" className="sidebar-link"><FaHeadset size={16} /> Soporte</Link>
        <Link to="/notificaciones" className="sidebar-link"><FaBell size={16} /> Notificaciones</Link>
    </div>
);

const DriverSidebarLinks = ({ driverStatus }) => (
    <div className="sidebar-links-container">
        {driverStatus === DRIVER_STATUSES.ACTIVE && (
            <>
                <div className="sidebar-section">Conducción</div>
                <Link to="/day-register" className="sidebar-link"><FaPlay size={16} /> Iniciar Jornada</Link>
                <Link to="/historial-servicios" className="sidebar-link"><FaList size={16} /> Historial de Servicios</Link>
                <Link to="/registrar" className="sidebar-link"><FaCar size={16} /> Mi Vehículo</Link>
            </>
        )}
        <div className="sidebar-section">Mi Cuenta</div>
        <Link to="/registro-conductor" className="sidebar-link">
            <FaIdCard size={16} /> {driverStatus === DRIVER_STATUSES.ACTIVE ? 'Perfil Conductor' : 'Completar Registro'}
        </Link>
        {driverStatus === DRIVER_STATUSES.ACTIVE && <Link to="/turnos" className="sidebar-link"><FaCalendarAlt size={16} /> Turnos</Link>}
        <Link to="/documentos" className="sidebar-link"><FaFileAlt size={16} /> Documentos</Link>
        <Link to="/metodos-pago" className="sidebar-link"><FaMoneyBillWave size={16} /> Métodos de Pago</Link>
        <Link to="/notificaciones" className="sidebar-link"><FaBell size={16} /> Notificaciones</Link>
        <Link to="/config" className="sidebar-link"><FaCog size={16} /> Configuración</Link>
        <div className="sidebar-section">Soporte</div>
        <Link to="/soporte" className="sidebar-link"><FaHeadset size={16} /> Soporte</Link>
    </div>
);

const Home = () => {
    const { currentUser, profile, isDriver, isPassenger, isAdmin, isSuperAdmin, hasPendingDriverRole } = useAuth();
    const { distanceToday, lastLocation, gpsOnline, setGpsOnline, gpsError, clearGpsError } = useGlobalGPS();

    const [showEarningModal, setShowEarningModal] = useState(false);
    const [showFuelModal, setShowFuelModal] = useState(false);
    const [showCalcModal, setShowCalcModal] = useState(false);
    const [showMaintModal, setShowMaintModal] = useState(false);

    const [pickupPos, setPickupPos] = useState(null);
    const [dropoffPos, setDropoffPos] = useState(null);
    const [selectedDriver, setSelectedDriver] = useState(null);

    const [installable, setInstallable] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const [driverStatus, setDriverStatus] = useState(null);
    const [vehicleModel, setVehicleModel] = useState('');

    useEffect(() => {
        // RBAC unificado: el estado de conductor aplica al driver ACTIVO o al
        // pendiente (rol inactivo esperando aprobacion del super admin).
        if (!currentUser || (!isDriver && !hasPendingDriverRole)) return;
        const fetchDriverStatus = async () => {
            try {
                const { data, error } = await getDriverStatus(currentUser.id);
                if (!error && data?.status) {
                    setDriverStatus(data.status);
                } else {
                    // Sin registro en tabla drivers → siempre PENDING hasta que admin apruebe
                    setDriverStatus(DRIVER_STATUSES.PENDING);
                }
            } catch (err) {
                console.error("Error fetching driver status:", err);
                setDriverStatus(DRIVER_STATUSES.PENDING);
            }
        };
        fetchDriverStatus();
    }, [currentUser, isDriver, profile]);

    useEffect(() => {
        if (window.deferredPrompt && !sessionStorage.getItem('pwa-banner-dismissed')) {
            setInstallable(true);
        }

        const handleInstallable = (e) => {
            if (!sessionStorage.getItem('pwa-banner-dismissed')) {
                setInstallable(e.detail);
            }
        };

        window.addEventListener('pwa-installable', handleInstallable);
        return () => {
            window.removeEventListener('pwa-installable', handleInstallable);
        };
    }, []);

    const handleInstallClick = async () => {
        const promptEvent = window.deferredPrompt;
        if (!promptEvent) return;
        promptEvent.prompt();
        const { outcome } = await promptEvent.userChoice;
        console.log(`User choice outcome: ${outcome}`);
        window.deferredPrompt = null;
        setInstallable(false);
    };

    const handleDismiss = () => {
        sessionStorage.setItem('pwa-banner-dismissed', 'true');
        setDismissed(true);
    };

    const clearPositions = () => { setPickupPos(null); setDropoffPos(null); setSelectedDriver(null); };

    const handleMapClick = (position) => {
        if (!pickupPos) { setPickupPos(position); }
        else if (!dropoffPos) { setDropoffPos(position); }
        else { setPickupPos(position); setDropoffPos(null); }
    };

    const [totalKm, setTotalKm] = useState(0);
    const [totalEarnings, setTotalEarnings] = useState(0);
    const [averageConsumption, setAverageConsumption] = useState(0);
    const [totalHoursWorked, setTotalHoursWorked] = useState(0);
    const [selectedPeriod, setSelectedPeriod] = useState("Semana");
    const [walletBalance, setWalletBalance] = useState(0);
    const [minWalletBalance, setMinWalletBalance] = useState(0);
    const [walletTransactions, setWalletTransactions] = useState([]);

    const [vehicleStats, setVehicleStats] = useState({
        fuelRange: '--', runsLeft: '--', oilRange: '--', chainRange: '--'
    });

    const fetchData = React.useCallback(async () => {
        if (!currentUser) return;
        try {
            const { startDate, endDate } = getDateRange(selectedPeriod);
            const userId = currentUser.id;
            
            const promises = [
                getEarnings(userId, startDate, endDate),
                getFuelings(userId),
                getLatestFueling(userId),
                getBikeData(userId)
            ];

            if (isDriver) {
                promises.push(getDriverWallet(userId).catch(err => {
                    console.warn("Error fetching wallet balance:", err);
                    return { balance: 0 };
                }));
                promises.push(getMinWalletBalance().catch(err => {
                    console.warn("Error fetching minimum wallet balance:", err);
                    return 0;
                }));
            }

            const results = await Promise.all(promises);
            const earningsData = results[0];
            const fuelingsData = results[1];
            const latestFuel = results[2];
            const vehicleData = results[3];
            const walletData = isDriver ? results[4] : null;
            const minBalanceValue = isDriver ? results[5] : 0;

            if (walletData) {
                setWalletBalance(walletData.balance || 0);
                setWalletTransactions(walletData.transactions || []);
            }
            if (isDriver) {
                setMinWalletBalance(minBalanceValue);
            }

            let tKm = 0, tEarnings = 0, tHours = 0, tFuel = 0;
            (earningsData || []).forEach(e => {
                tEarnings += parseFloat(e.amount || 0);
                tKm += parseFloat(e.mileage || 0);
                tHours += parseFloat(e.duration || e.hours || 0) / 60;
            });
            (fuelingsData || []).forEach(f => tFuel += parseFloat(f.litros || 0));
            setTotalKm(tKm); setTotalEarnings(tEarnings); setTotalHoursWorked(tHours);
            setAverageConsumption(tFuel > 0 ? (tKm / tFuel) : 0);

            if (vehicleData) {
                if (vehicleData.model) {
                    setVehicleModel(vehicleData.model);
                }
                if (latestFuel) {
                    const consumption = vehicleData.avg_consumption || 35;
                    const usedFuel = (distanceToday || 0) / consumption;
                    const remaining = Math.max(0, (latestFuel.litros || 10) - usedFuel);
                    const remainingKm = remaining * consumption;
                    setVehicleStats({
                        fuelRange: `${remainingKm.toFixed(0)} km`,
                        runsLeft: `${Math.floor(remainingKm / 5)} carreras`,
                        oilRange: `${Math.max(0, 3000 - (tKm % 3000))} km`,
                        chainRange: `${Math.max(0, 15000 - (tKm % 15000))} km`
                    });
                }
            }
        } catch (err) { console.error("Error al cargar datos del Home:", err); }
    }, [currentUser, selectedPeriod, distanceToday, isDriver]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const getDateRange = (period) => {
        const now = new Date();
        let startDate;
        switch (period) {
            case "Hoy": startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()); break;
            case "Semana": startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000); break;
            case "Quincenal": startDate = new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000); break;
            case "Mes": startDate = new Date(now.getFullYear(), now.getMonth(), 1); break;
            default: startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        }
        return { startDate, endDate: now };
    };

    const earningPerKm = totalKm > 0 ? (totalEarnings / totalKm).toFixed(2) : '0.00';

    const displayName = currentUser?.user_metadata?.full_name
        || currentUser?.user_metadata?.name
        || 'Usuario';

    const renderSidebarLinks = () => {
        if (isAdmin || isSuperAdmin) return <AdminSidebarLinks />;
        if (isDriver || hasPendingDriverRole || driverStatus === DRIVER_STATUSES.PENDING) {
            return <DriverSidebarLinks driverStatus={driverStatus} />;
        }
        return <PassengerSidebarLinks />;
    };

    const renderRoleView = () => {
        if (isAdmin || isSuperAdmin) {
            return <AdminView
                pickupPos={pickupPos} dropoffPos={dropoffPos} handleMapClick={handleMapClick}
                selectedDriver={selectedDriver} setSelectedDriver={setSelectedDriver} clearPositions={clearPositions}
            />;
        }
        if (isDriver || hasPendingDriverRole || driverStatus === DRIVER_STATUSES.PENDING) {
            return <DriverView 
                driverStatus={driverStatus}
                gpsOnline={gpsOnline} setGpsOnline={setGpsOnline} gpsError={gpsError} clearGpsError={clearGpsError}
                selectedPeriod={selectedPeriod} setSelectedPeriod={setSelectedPeriod} 
                totalEarnings={totalEarnings} totalKm={totalKm} totalHoursWorked={totalHoursWorked} 
                earningPerKm={earningPerKm} averageConsumption={averageConsumption} bikeStats={vehicleStats}
                pickupPos={pickupPos} dropoffPos={dropoffPos} handleMapClick={handleMapClick}
                selectedDriver={selectedDriver} setSelectedDriver={setSelectedDriver} clearPositions={clearPositions}
                setShowEarningModal={setShowEarningModal} setShowFuelModal={setShowFuelModal}
                setShowCalcModal={setShowCalcModal} setShowMaintModal={setShowMaintModal}
                walletBalance={walletBalance}
                minWalletBalance={minWalletBalance}
                walletTransactions={walletTransactions}
            />;
        }
        return <PassengerView 
            pickupPos={pickupPos} dropoffPos={dropoffPos} handleMapClick={handleMapClick}
            selectedDriver={selectedDriver} setSelectedDriver={setSelectedDriver} clearPositions={clearPositions}
        />;
    };

    return (
        <div className="home-container">
            <div className="home-desktop-layout">
                <div className="home-sidebar">
                    {renderSidebarLinks()}
                </div>
                <div className="home-content">
                    {(isAdmin || isSuperAdmin) && (
                        <HeaderSection 
                            displayName={displayName} isDriver={isDriver} isPassenger={isPassenger} 
                            isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} vehicleModel={vehicleModel} 
                            distanceToday={distanceToday} lastLocation={lastLocation} gpsOnline={gpsOnline} 
                            setGpsOnline={setGpsOnline} gpsError={gpsError} clearGpsError={clearGpsError}
                            setShowCalcModal={setShowCalcModal} 
                        />
                    )}
                    <InstallBanner 
                        installable={installable} dismissed={dismissed} 
                        handleInstallClick={handleInstallClick} handleDismiss={handleDismiss} 
                    />
                    
                    {renderRoleView()}

                    <HomeModals 
                        showEarningModal={showEarningModal} setShowEarningModal={setShowEarningModal} 
                        showFuelModal={showFuelModal} setShowFuelModal={setShowFuelModal} 
                        showCalcModal={showCalcModal} setShowCalcModal={setShowCalcModal} 
                        showMaintModal={showMaintModal} setShowMaintModal={setShowMaintModal} 
                        fetchData={fetchData} 
                    />
                </div>
            </div>
        </div>
    );
};

export default Home;
